import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ActionBar from "../../../Components/Button/ActionBar";
import NewsMediaForm from "../../../Components/Forms/NewsMediaForm";
import Delete from "../../../Components/Button/Delete";
import axiosRetry from "axios-retry";
import { toast } from "sonner";

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: any) => {
    return error.response?.status === 429;
  },
});

export default function NewsMedia() {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state && location.state.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const isMounted = useRef(false);

  const [year, setYear] = useState("");
  const [artist, setArtist] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [media, setMedia] = useState("");
  const [datePosted, setDatePosted] = useState("");
  const [link, setLink] = useState("");
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnail_name, setThumbnailName] = useState<string>("");

  // Handle form input changes
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setYear(e.target.value);
  const handleArtistChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setArtist(e.target.value);
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setCategory(e.target.value);
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTitle(e.target.value);
  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMedia(e.target.value);
  const handleDatePostedChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDatePosted(e.target.value);
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLink(e.target.value);
 
  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast.error(<p className="text-[16px]">Thumbnail image size should not exceed 2MB.</p>, {
          duration: 1500,
        });

        return;
      }

      setThumbnail(file);
    }
  };

  const buildFormData = (): FormData | null => {
    if ( !year || !artist || !category || !title || !media || !datePosted || !link) {
      toast.error(<p className="text-[16px]">Please fill all required fields.</p>, {
        duration: 1500,
      });

      return null;
    }

    // Require thumbnail only when creating (no id)
    if (!id && !thumbnail) {
      toast.error(<p className="text-[16px]">Please upload a thumbnail image.</p>, {
        duration: 1500,
      });

      return null;
    }

    // Validate Link format (basic)
    try {
      new URL(link);
    } catch (_) {
      toast.error(<p className="text-[16px]">Please enter a valid URL for the Link field.</p>, {
        duration: 1500,
      });

      return null;
    }

    const formData = new FormData();
    formData.append("Year", year); // Match database column name
    formData.append("Artist", artist); // Match database column name
    formData.append("Category", category); // Match database column name
    formData.append("Title", title); // Match database column name
    formData.append("Media", media); // Match database column name
    formData.append("DatePosted", datePosted); // Match database column name
    formData.append("Link", link); // Match database column name

    if (thumbnail) {
      formData.append("Thumbnail", thumbnail, thumbnail.name); // Match database column name
    }

    if (id) {
      formData.append("_method", "PUT");
    }

    return formData;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData = buildFormData();
    if (!formData) {
      setIsSubmitting(false);
      return;
    }

    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.post("https://cdn.pulp.ph/api/v1/pulpliveworld/news-media", formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(<p className="text-[16px]">Content submitted successfully.</p>, {
        duration: 1500,
      });

      setTimeout(() => {
        navigate("/view/post/news-Media");
      }, 1600);
    } catch (error) {
      console.error("An error occurred during form submission!", error);

      toast.error(<p className="text-[16px]">An error occurred during form submission!</p>, {
        duration: 1500,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle save as draft
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);

    const formData = buildFormData();
    if (!formData) {
      setIsSaving(false);
      return;
    }
    // ...existing code...
  };

  // Handle deletion
  const handleDelete = async () => {
    if (!id) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this content?"
    );
    if (!confirmDelete) return;

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Authentication token not found!");
        return;
      }

      await axios.delete(`https://cdn.pulp.ph/api/v1/pulpliveworld/news-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      toast.success(<p className="text-[16px]">Content deleted successfully.</p>, {
        duration: 1500,
      });

      setTimeout(() => {
        navigate("/view/post/newsMedia");
      }, 1600);
    } catch (error) {
      console.error("Unable to delete. Please try again.", error);
      
      toast.error(<p className="text-[16px]">Unable to delete. Please try again.</p>, {
        duration: 1500,
      });
    }
  };

  // Fetch existing data if editing
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(`https://cdn.pulp.ph/api/v1/pulpliveworld/news-media/${id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              Accept: "application/json",
            },
          }
        );

        const data = response.data.data || response.data;
        setYear(data.year || "");
        setArtist(data.artist || "");
        setCategory(data.category || "");
        setTitle(data.title || "");
        setMedia(data.media || "");
        setDatePosted(data.date_posted ? data.date_posted.split("T")[0] : "");
        setLink(data.link || "");
        setThumbnailName(data.thumbnail_name || "");
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id]);

  // Set role on mount
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);
  }, []);

  return (
    <div className="w-full p-5">
      <div className="flex justify-between items-center mb-4">
        <h1 className="GraphikSemibold text-[20px] font-semibold">Create News & Media</h1>
        <ActionBar
          role={role}
          onSubmit={handleSubmit}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          isSaved={isSaving}
        />
      </div>
      
      <div className="py-5 px-4 rounded bg-white">
        <NewsMediaForm
          year={year}
          artist={artist}
          category={category}
          title={title}
          media={media}
          datePosted={datePosted}
          link={link}
          thumbnail={thumbnail}
          thumbnail_name={thumbnail_name}
          onYearChange={handleYearChange}
          onArtistChange={handleArtistChange}
          onCategoryChange={handleCategoryChange}
          onTitleChange={handleTitleChange}
          onMediaChange={handleMediaChange}
          onDatePostedChange={handleDatePostedChange}
          onLinkChange={handleLinkChange}
          onThumbnailChange={handleThumbnailChange}
        />
      </div>
    </div>
  );
}
